'use client'

import type { NavItem } from '@/constants'
import type { CSSProperties, Dispatch, RefObject, SetStateAction } from 'react'

import parse from 'html-react-parser'
import { has } from 'lodash'

import right_arrow from '@/assets/images/right_arrow.svg'
import Link from '@/components/Link'
import { formatTitleWithItalic } from '@/utils'
import { isSamePath } from '@/utils/href'

/**
 * The desktop mega-menu (`<nav id="navmenu">`), lifted out of MainHeader
 * verbatim — every className, aria attribute and handler is unchanged.
 *
 * State stays in MainHeader and arrives as explicit props: the mobile menu
 * derives from the same `tabDesktopContent`, so ownership has to live above
 * both. No context, no store additions.
 */
export function DesktopMegaMenu({
  tabDesktopContent,
  activeChild,
  setActiveChild,
  activeGrandChild,
  setActiveGrandChild,
  style,
  setStyle,
  parentRef,
  pathname,
  router,
  setCreditCardData,
  isMenuOpen,
  toggleMenu
}: {
  tabDesktopContent: NavItem[]
  activeChild: NavItem | null
  setActiveChild: Dispatch<SetStateAction<NavItem | null>>
  activeGrandChild: number
  setActiveGrandChild: Dispatch<SetStateAction<number>>
  style: CSSProperties
  setStyle: Dispatch<SetStateAction<CSSProperties>>
  parentRef: RefObject<HTMLUListElement | null>
  pathname: string
  router: { push: (href: string) => void }
  setCreditCardData: (data: string | object) => void
  isMenuOpen: boolean
  toggleMenu: () => void
}) {
  return (
    <nav id="navmenu" className="navmenu">
      <ul className="parent-level" ref={parentRef}>
        {tabDesktopContent.map(menu => {
          return (
            <li key={menu.title} className="listing-dropdown">
              <Link
                href={menu.path || '#'}
                onClick={e => {
                  // Top-level entries are dropdown triggers without a
                  // destination; don't navigate (and don't jump to top).
                  if (!menu.path) e.preventDefault()
                }}
                onMouseEnter={() => {
                  setActiveChild(menu)
                  setStyle({
                    visibility: 'visible',
                    opacity: 1,
                    top: '100%',
                    transition: '0.3s all ease-in'
                  })
                }}
                className={
                  (Array.isArray(menu.parent) ? menu.parent : [menu.parent])
                    .filter((p): p is string => Boolean(p))
                    .some(p => pathname.includes(p))
                    ? `active`
                    : ``
                }
              >
                <span>{typeof menu.title === 'string' ? parse(menu.title) : menu.title}</span>
              </Link>
            </li>
          )
        })}
        <ul id="first-level" style={style} data-aos="fade-down">
          {activeChild?.children?.map((child, index) => (
            <li key={`child-${child.type ?? child.title ?? index}`} className="child-level">
              <p
                style={{
                  color: '#000045',
                  marginBottom: '10px',
                  padding: '10px 20px 0 20px'
                }}
              >
                {/* If type exists, otherwise add space between title and first child */}
                {child.type ? parse(child.type) : '\u00A0'}
              </p>
              <div>
                {child.action === 'Click-and-show' ? (
                  <div className="d-flex justify-content-start align-items-start">
                    <div>
                      {(child.children ?? []).map((item, index) => (
                        <p
                          key={item.title}
                          className="banca-takaful"
                          onClick={() => setActiveGrandChild(index)}
                          style={{
                            color:
                              index === activeGrandChild
                                ? 'var(--alrajhi-black)'
                                : 'var(--grey-500-body-text)',
                            fontWeight: index === activeGrandChild ? 700 : 400,
                            cursor: 'pointer'
                          }}
                        >
                          {item.title && parse(item.title)}
                          <img
                            src={right_arrow.src}
                            alt="Right Arrow Icon"
                            width="15px"
                            className="pb-1 ms-1"
                          />
                        </p>
                      ))}
                    </div>
                    <div
                      className={`d-flex ${
                        child.children?.[activeGrandChild]?.orientation === 'horizontal'
                          ? 'flex-row'
                          : 'flex-column'
                      } banca-takaful-item`}
                    >
                      {(child.children?.[activeGrandChild]?.children ?? []).map(
                        (subItem, subIdx) => {
                          return has(subItem, 'children') ? (
                            <Link
                              key={subIdx}
                              href={subItem.path ?? ''}
                              className={isSamePath(pathname, subItem.path) ? 'active' : ''}
                            >
                              {subItem.title && parse(subItem.title)}
                            </Link>
                          ) : (
                            <div
                              key={subIdx}
                              className={`${subItem.image ? `my-3` : ``} text-center
                                `}
                              onClick={() => {
                                setStyle({
                                  opacity: 0,
                                  visibility: 'hidden',
                                  top: '100%',
                                  transition: '0.3s all ease-in'
                                })
                                if (subItem?.category) {
                                  setCreditCardData(subItem.category)
                                }
                              }}
                            >
                              {subItem.image && (
                                <img
                                  src={subItem.image}
                                  alt={subItem.title}
                                  className="ms-3 cursor-pointer"
                                  width={
                                    child.children?.[activeGrandChild]?.orientation === 'horizontal'
                                      ? '100'
                                      : '200'
                                  }
                                  onClick={() => router.push(subItem.path ?? '')}
                                />
                              )}
                              <Link
                                href={subItem.path ?? ''}
                                style={{ textAlign: 'center' }}
                                className={isSamePath(pathname, subItem.path) ? 'active' : ''}
                              >
                                {parse(subItem.title ?? '')}
                              </Link>
                            </div>
                          )
                        }
                      )}
                    </div>
                  </div>
                ) : (
                  (child.children ?? []).map((item, itemIdx) => {
                    return has(item, 'children') ? (
                      <div
                        key={itemIdx}
                        className="d-flex justify-content-start align-items-start"
                        onClick={() => {
                          if (child.action !== 'Click-and-show') {
                            setStyle({
                              opacity: 0,
                              visibility: 'hidden',
                              top: '100%',
                              transition: '0.3s all ease-in'
                            })
                          }
                        }}
                      >
                        <p className="banca-takaful">
                          {item.title && formatTitleWithItalic(parse(item.title))}
                          <img
                            src={right_arrow.src}
                            alt="Right arrow"
                            width="15px"
                            className="pb-1 ms-1"
                          />
                        </p>
                        <div
                          className={`d-flex ${
                            item.orientation === 'horizontal' ? 'flex-row' : 'flex-column'
                          } banca-takaful-item`}
                        >
                          {(item.children ?? []).map((subItem, subIdx) => {
                            return has(subItem, 'children') ? (
                              <Link
                                key={subIdx}
                                href={subItem.path ?? ''}
                                className={isSamePath(pathname, subItem.path) ? 'active' : ''}
                              >
                                {subItem.title && parse(subItem.title)}
                              </Link>
                            ) : (
                              <div
                                key={subIdx}
                                className={`${subItem.image ? `my-3` : ``}
                                `}
                                onClick={() => {
                                  setStyle({
                                    opacity: 0,
                                    visibility: 'hidden',
                                    top: '100%',
                                    transition: '0.3s all ease-in'
                                  })
                                }}
                              >
                                {subItem.image && (
                                  <img
                                    src={subItem.image}
                                    alt={subItem.title}
                                    className="ms-3 cursor-pointer"
                                    width={item.orientation === 'horizontal' ? '100' : '200'}
                                    onClick={() => router.push(subItem.path ?? '')}
                                  />
                                )}
                                <Link
                                  href={subItem.path ?? ''}
                                  className={isSamePath(pathname, subItem.path) ? 'active' : ''}
                                >
                                  {parse(subItem.title ?? '')}
                                </Link>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ) : (
                      <div
                        key={itemIdx}
                        className={item.image ? `my-3` : ``}
                        onClick={() => {
                          setStyle({
                            opacity: 0,
                            visibility: 'hidden',
                            top: '100%',
                            transition: '0.3s all ease-in'
                          })
                        }}
                      >
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="ms-3 cursor-pointer"
                            width="200"
                            onClick={() => router.push(item.path ?? '')}
                          />
                        )}
                        <Link
                          href={item.path ?? ''}
                          className={isSamePath(pathname, item.path) ? 'active' : ''}
                        >
                          {parse(item.title ?? '')}
                        </Link>
                      </div>
                    )
                  })
                )}
              </div>
            </li>
          ))}
        </ul>
      </ul>
      <button
        type="button"
        className="mobile-nav-toggle d-xl-none bi bi-list bg-transparent border-0"
        aria-label="Toggle navigation menu"
        aria-expanded={isMenuOpen}
        aria-controls="mobile-nav"
        onClick={toggleMenu}
      ></button>
    </nav>
  )
}
